// src/components/Header.jsx
import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { auth, db } from "../firebase";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, onSnapshot } from "firebase/firestore";
import logo from "../assets/logo-baccapsule.png";
import { matiereToSlug } from "../authHelpers";

export default function Header() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState("guest"); // guest | eleve | prof | admin
  const [subject, setSubject] = useState(null); // matière du prof
  const [scrolled, setScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const unsubAuth = onAuthStateChanged(auth, (u) => {
      setUser(u || null);
      if (!u) {
        setRole("guest");
        return;
      }
      const unsubDoc = onSnapshot(
        doc(db, "users", u.uid),
        (snap) => {
          const d = snap.data() || {};
          setRole(d.role || "guest");
          setSubject(d.subject || d.matiere || null);

          // 🔐 Vérif de session: si un autre appareil s'est connecté,
          // le token Firestore change et on déconnecte cet appareil.
          const currentToken = localStorage.getItem("sessionToken");
          if (d.sessionToken && currentToken && d.sessionToken !== currentToken) {
            alert("Votre compte a été ouvert sur un autre appareil. Déconnexion en cours.");
            try { localStorage.removeItem("sessionToken"); } catch {}
            signOut(auth);
          }
        },
        (err) => {
          console.error("users doc listener error", err);
          // Revenir à un état invité pour éviter les erreurs non gérées
          setRole("guest");
          setSubject(null);
        }
      );
      return () => unsubDoc();
    });
    return () => unsubAuth();
  }, []);

  useEffect(() => {
    // Animation d'entrée + gestion du scroll
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    const t = setTimeout(() => setMounted(true), 10);
    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(t);
    };
  }, []);

  const isAdmin = role === "admin";
  const isProf = role === "prof";
  const subjectSlug = (matiereToSlug(subject || "") || "").toLowerCase();
  const is3ACFrProf = isProf && ["fr", "ar", "ei", "hg"].includes(subjectSlug);
  // Toujours visible pour que l'accès soit évident (redirigé selon rôle si nécessaire)
  const showProfLink = true;
  // Libellé élève: avant connexion → "Espace élève", après → "Espace élève 2BAC"
  const eleveLabel = user ? "Espace élève 2BAC" : "Espace élève";

  // Helpers de classes
  const navLinkClass = ({ isActive }) =>
    `nav-link fx-underline ${isActive ? "active" : ""}`;

  const handleSignOut = async () => {
    try { localStorage.removeItem("sessionToken"); } catch {}
    await signOut(auth);
  };

  return (
    <>
      <nav
        className={`navbar navbar-expand-lg navbar-light border-bottom sticky-top glass ${
          scrolled ? "is-scrolled" : ""
        } ${mounted ? "is-mounted" : ""}`}
      >
        <div className="container">
          <Link className="navbar-brand d-flex align-items-center gap-2 brand-link" to="/">
            <img
              src={logo}
              alt="BacCapsule"
              width={28}
              height={28}
              className="rounded-3 logo-spin"
            />
            <span className="fw-bold brand-gradient">Edu 2025</span>
          </Link>

          {/* hamburger mobile */}
          <button
            className="navbar-toggler focus-ring"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#mainNav"
            aria-controls="mainNav"
            aria-expanded="false"
            aria-label="Basculer la navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          <div className="collapse navbar-collapse" id="mainNav">
            <ul className="navbar-nav ms-auto align-items-lg-center">
              {/* Contenu gratuit — visible pour tous, même sans connexion */}
              <li className="nav-item">
                <NavLink className={navLinkClass} to="/gratuit">
                  <span className="fx-link-text">Contenu gratuit</span>
                </NavLink>
              </li>

              {/* Élève (renommé 2BAC) — caché pour profs 3AC Fr (Français, Arabe, EI, HG) */}
              {!is3ACFrProf && (
                <li className="nav-item">
                  <NavLink className={navLinkClass} to="/eleve">
                    <span className="fx-link-text">{eleveLabel}</span>
                  </NavLink>
                </li>
              )}

              {/* Visible pour invités + prof + admin */}
              {showProfLink && (
                <li className="nav-item">
                  <NavLink className={navLinkClass} to="/prof">
                    <span className="fx-link-text">Espace enseignant</span>
                  </NavLink>
                </li>
              )}


              {/* Lien dédié admin vers espace élève 3AC Fr */}
              {isAdmin && (
                <li className="nav-item">
                  <NavLink className={navLinkClass} to="/eleve?section=3AC-Fr">
                    <span className="fx-link-text">Espace élève 3AC Fr</span>
                  </NavLink>
                </li>
              )}

              {/* Visible uniquement pour admin */}
              {isAdmin && (
                <li className="nav-item">
                  <NavLink className={navLinkClass} to="/admin">
                    <span className="fx-link-text">Admin</span>
                  </NavLink>
                </li>
              )}

              <li className="nav-item ms-lg-3">
                {user ? (
                  <button
                    className="btn btn-outline-secondary btn-sm btn-float"
                    onClick={handleSignOut}
                  >
                    Se déconnecter
                  </button>
                ) : (
                  <NavLink className={() => "btn btn-primary btn-sm btn-float"} to="/login">
                    Se connecter
                  </NavLink>
                )}
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Styles locaux (aucune dépendance supplémentaire) */}
      <style>{`
        /* Respecte les préférences d'accessibilité */
        @media (prefers-reduced-motion: reduce) {
          .logo-spin { transition: none !important; }
          .fx-underline::after { transition: none !important; }
          .btn-float { transition: none !important; }
          .brand-gradient { background-size: auto !important; animation: none !important; }
          .glass { transition: background-color .2s ease, box-shadow .2s ease !important; }
        }

        /* Apparence générale (verre flouté + ombre douce au scroll) */
        .glass {
          backdrop-filter: saturate(1.2) blur(6px);
          -webkit-backdrop-filter: saturate(1.2) blur(6px);
          background: rgba(255,255,255,0.82);
          transition: background-color .35s ease, box-shadow .35s ease, transform .35s ease;
          will-change: background-color, box-shadow, transform;
          transform: translateY(-12px);
          opacity: 0;
        }
        .glass.is-mounted { transform: translateY(0); opacity: 1; }
        .glass.is-scrolled { box-shadow: 0 8px 24px rgba(16,24,40,.06), 0 2px 6px rgba(16,24,40,.05); }

        /* Logo: légère rotation au survol */
        .logo-spin { transition: transform .6s cubic-bezier(.2,.8,.2,1); }
        .brand-link:hover .logo-spin { transform: rotate(8deg) scale(1.04); }

        /* Texte de marque: dégradé animé (shimmer) */
        .brand-gradient {
          background: linear-gradient(90deg, #0d6efd, #6f42c1, #20c997, #0d6efd);
          -webkit-background-clip: text; background-clip: text; color: transparent;
          background-size: 300% 100%;
          animation: shimmer 6s ease-in-out infinite;
          letter-spacing: .2px;
        }
        @keyframes shimmer {
          0%{background-position:0% 50%}
          50%{background-position:100% 50%}
          100%{background-position:0% 50%}
        }

        /* Liens du menu avec soulignement animé */
        .fx-underline { position: relative; }
        /* Uniformiser couleur/poids des onglets (pas de variation active/hover) */
        .nav-link.fx-underline { color: #212529; font-weight: 500; }
        .nav-link.fx-underline:hover { color: #212529; }
        .nav-link.fx-underline.active { color: #212529; }
        .fx-underline::after {
          content: "";
          position: absolute;
          left: 0; bottom: -4px;
          height: 2px; width: 0%;
          background: currentColor;
          transition: width .25s ease;
        }
        /* Soulignement uniquement au survol (pas en état actif) */
        .fx-underline:hover::after { width: 100%; }

        .btn-float { transition: transform .2s ease; }
        .btn-float:hover { transform: translateY(-1px); }
      `}</style>
    </>
  );
}
